import { MetadataField } from '../../../../../../../../../constnats/reportConstants';
import Assert from '../../../../../../../../../utils/asserts';
import ObjCheck from '../../../../../../../../../utils/objCheck';
import { ERROR_PROMPTING_IMPACT_FIELD_PROPS, FIELD_PROP } from '../../../../../../constants/generalConstants';
import { FieldDiffType, FieldPropDiffType, ImpactType } from '../../../../../../types';

type DescriptorToPropsMapType = { [descriptorId: string]: Set<string> };

type ReportDiff = {
    originalValue: any;
    modifiedValue: any;
    requireManualCheck: boolean;
};

type ReportToPropDiffMapType = { [reportId: number]: ReportDiff };
type PropType = {
    propName: string;
    reportToPropDiff: ReportToPropDiffMapType;
};

type DescriptorDiffsType = {
    descriptorId: string;
    props: PropType[];
};

type TableModelType = { rows: DescriptorDiffsType[] };

function getDescriptorToPropsMap(impact: ImpactType): DescriptorToPropsMapType {
    const descriptorToProps: DescriptorToPropsMapType = {};
    for (const reportDiff of impact.updatedReports) {
        for (const fieldDiff of reportDiff.diffs) {
            const descriptorId = fieldDiff.descriptorId;

            if (!descriptorToProps[descriptorId]) {
                descriptorToProps[descriptorId] = new Set<string>();
            }

            for (const fieldPropDiff of fieldDiff.diffs) {
                descriptorToProps[descriptorId].add(fieldPropDiff.propName);
            }
        }
    }

    return descriptorToProps;
}

const PROPERTIES_TO_IGNORE = [MetadataField.INPUT_SCALE];

export function getImpactTableModel(impact: ImpactType, importId: number): TableModelType {
    const descriptorToProps = getDescriptorToPropsMap(impact);
    const reportIds = impact.updatedReports.map((reportDiff) => reportDiff.id);


    function isFalsePositiveDiff(propDiff: FieldPropDiffType, fieldDiff: FieldDiffType) {
        let isFalsePositive = false;
        if (propDiff.propName === FIELD_PROP.CORRECTION && propDiff.originalValue === '0') {
            const coreValue = fieldDiff.diffs.find((diff) => diff.propName === FIELD_PROP.CORE_VALUE);
            Assert.trueVal(!!coreValue,`Core value is missing from fieldDiff with id: ${fieldDiff.descriptorId} and import id: ${importId}`)
            if (ObjCheck.isNullUndefinedEmptyOrDash(coreValue?.originalValue)) {
                isFalsePositive = true
            }
        }

        return isFalsePositive;
    }

    const tableModel: TableModelType = {
        rows: Object.entries(descriptorToProps).map(([descriptorId, propsSet]) => {
            const properties = Array.from(propsSet);
            const descriptor: DescriptorDiffsType = {
                descriptorId,
                props: properties
                    .filter((propName) => !PROPERTIES_TO_IGNORE.includes(propName))
                    .map((propName) => {
                        return {
                            propName,
                            reportToPropDiff: reportIds.reduce((acc, reportId) => {
                                const report = impact.updatedReports.find((r) => r.id === reportId);
                                const fieldDiff = report?.diffs.find((fd) => fd.descriptorId === descriptorId);
                                let propDiff = fieldDiff?.diffs.find((pd) => pd.propName === propName);

                                let requireManualCheck = false;

                                if (propDiff) {
                                    if (isFalsePositiveDiff(propDiff, fieldDiff!)) {
                                        /**
                                         * TODO: Find a better non mutation way of skipping false positives
                                         * Here because this is a false positive we set it to be undefined
                                         */
                                        propDiff = undefined;
                                    } else if (ERROR_PROMPTING_IMPACT_FIELD_PROPS.includes(propDiff.propName) &&
                                        !ObjCheck.isNullUndefinedOrEmpty(propDiff.originalValue) &&
                                        !ObjCheck.isNullUndefinedOrEmpty(propDiff.modifiedValue)) {
                                        requireManualCheck = true;
                                    }
                                }

                                acc[reportId] = {
                                    originalValue: propDiff?.originalValue,
                                    modifiedValue: propDiff?.modifiedValue,
                                    requireManualCheck,
                                };

                                return acc;
                            }, {} as ReportToPropDiffMapType),
                        };
                    }),
            };

            return descriptor;
        }),
    };

    return tableModel;
}

export function hasErrorPromptingFieldProps(tableModel: TableModelType): boolean {
    return tableModel.rows.some((row) =>
        row.props.some((prop) => Object.values(prop.reportToPropDiff).some((reportDiff) => reportDiff.requireManualCheck))
    );
}

export function getReportCellClasses(requireManualCheck: boolean): string {
    let classes = '';
    if (requireManualCheck) {
        classes = `${classes} warning`;
    }

    return classes;
}
